import { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /** Marks the field as required */
  required?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, helperText, id, rows = 4, required, ...props }, ref) => {
    // Generate stable ID for label-textarea association
    const textareaId = id || (label ? `textarea-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);
    const errorId = error ? `${textareaId}-error` : undefined;
    const helperId = helperText ? `${textareaId}-helper` : undefined;
    
    // Combine aria-describedby values
    const ariaDescribedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-slate-300 mb-1.5"
          >
            {label}
            {required && <span className="text-red-400 ml-1" aria-hidden="true">*</span>}
            {required && (
              <span className="sr-only"> (erforderlich)</span>
            )}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={ariaDescribedBy}
          aria-required={required}
          className={clsx(
            'w-full px-4 py-2.5 bg-slate-900 border rounded-lg text-white placeholder-slate-500',
            // Enhanced focus styles with visible ring
            'focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500',
            'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950',
            'transition-all duration-200 resize-vertical',
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/50'
              : 'border-slate-700 hover:border-slate-600',
            className
          )}
          {...props}
        />
        {error && (
          <p 
            id={errorId}
            className="mt-1.5 text-sm text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="mt-1.5 text-sm text-slate-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
